import type { AccountCloudPosture } from './accountStateMachine.js'
import { BYTES_PER_KILOBYTE } from '../constants.js'
import { parseStructuredIdDate } from '../id.js'
import type { LocalSpaceAvailability } from './databaseMemory.js'
import type { SyncSpaceMeta } from './syncSpaces.js'

export function formatMemoryRowDescription(input: {
  availability: LocalSpaceAvailability
  sizeBytes?: number
  spaceEd?: string
}): string {
  if (input.availability === 'unavailable') return 'cloud'
  if (input.availability === 'damaged') return 'error'

  const facts = ['local']
  const sizeBytes = input.sizeBytes
  if (typeof sizeBytes === 'number' && Number.isFinite(sizeBytes) && sizeBytes > 0) {
    facts.push(`${Math.ceil(sizeBytes / BYTES_PER_KILOBYTE)} KB`)
  }
  const editedDate = input.spaceEd
    ? parseStructuredIdDate(input.spaceEd)
    : undefined
  if (editedDate) {
    facts.push(`edited ${editedDate.year} ${editedDate.month} ${editedDate.day}`)
  }
  return facts.join(' | ')
}

export function shouldCollapsePaletteAfterIndexNavigation(
  currentSpaceId: string | undefined,
  targetSpaceId: string
): boolean {
  return Boolean(currentSpaceId) && currentSpaceId === targetSpaceId
}

export type ActiveMemoryShelfRow = {
  owner_id: string | null
  deleted_at: string | null
}

export type ActiveMemoryShelfSlugRow = ActiveMemoryShelfRow & {
  space_id: string
  space_slug: string | null
}

export function selectLiveShelfRows<T extends { deleted_at?: string | null }>(rows: T[]): T[] {
  return rows.filter(row => !row.deleted_at)
}

export function selectLandingSpace<T extends {
  space_id: string
  space_ed?: string | null
  deleted_at?: string | null
}>(spaces: T[], opts?: { excludeSpaceId?: string }): T | undefined {
  let landing: T | undefined
  for (const space of spaces) {
    if (space.deleted_at || space.space_id === opts?.excludeSpaceId) continue
    if (!landing) {
      landing = space
      continue
    }
    const landingEd = (landing.space_ed ?? '').trim()
    const candidateEd = (space.space_ed ?? '').trim()
    if (
      candidateEd > landingEd
      || (candidateEd === landingEd && space.space_id > landing.space_id)
    ) {
      landing = space
    }
  }
  return landing
}

export function projectSyncSpaceMeta(row: {
  space_id: string
  space_slug: string | null
  space_ed: string
  sync_at: string | null
  size_bytes: number
  deleted_at: string | null
}): SyncSpaceMeta {
  return {
    space_id: row.space_id,
    space_slug: row.space_slug,
    space_ed: row.space_ed,
    sync_at: row.sync_at,
    size_bytes: row.size_bytes,
    deleted_at: row.deleted_at
  }
}

export function selectActiveMemoryShelfRows<T extends ActiveMemoryShelfRow>(
  rows: T[],
  ownerId: string | null
): T[] {
  return selectLiveShelfRows(rows).filter(row => row.owner_id === ownerId)
}

export function selectActiveMemoryShelfRowBySlug<T extends ActiveMemoryShelfSlugRow>(
  rows: T[],
  ownerId: string | null,
  slug: string,
  normalizeSlug: (spaceSlug: string, spaceId: string) => string
): T | undefined {
  return selectActiveMemoryShelfRows(rows, ownerId).find(row => {
    return normalizeSlug(row.space_slug ?? '', row.space_id) === slug
  })
}

export type ShelfLane = {
  ownerId: string | null
}

export type ActiveShelfOpenStatus = {
  ok: boolean
  reason?: 'missing' | 'missing-payload' | 'invalid-payload'
}

export type MemoryOpenFailureReason = NonNullable<ActiveShelfOpenStatus['reason']> | 'read-failed'

export function selectMemoryOpenFailureMessage(reason?: MemoryOpenFailureReason): string {
  if (reason === 'invalid-payload') return 'memory damaged'
  if (reason === 'missing-payload' || reason === 'read-failed') return 'memory unavailable'
  return 'not in memory'
}

export function isCloudBackedShelfLane(lane: ShelfLane): boolean {
  return Boolean(lane.ownerId)
}

export function shouldHydrateMissingShelfPayload(
  lane: ShelfLane,
  opened: ActiveShelfOpenStatus,
  authority: {
    mountedAccountOwnerId: string | null
    cloudPosture: AccountCloudPosture
  }
): boolean {
  return isCloudBackedShelfLane(lane)
    && lane.ownerId === authority.mountedAccountOwnerId
    && !opened.ok
    && opened.reason === 'missing-payload'
    && authority.cloudPosture === 'connected-verified'
}

export type ShelfExportSource = 'local' | 'cloud'

export function selectShelfExportSource(lane: ShelfLane): ShelfExportSource {
  return isCloudBackedShelfLane(lane) ? 'cloud' : 'local'
}

export type AnonymousLoginGateChoice = 'import' | 'delete' | 'cancel'
export type DeactivatedLoginLocalMemoryChoice = 'export-and-continue' | 'delete-and-continue' | 'cancel'

export const DEFAULT_SPACE_SLUG_SUFFIX = 'lilo-space'
export const LEGACY_SPACE_SLUG_SUFFIX = 'lilo-data'

export type AnonymousLoginGateRow = {
  space_id: string
  space_slug: string | null
}

export function spaceIdToDbName(spaceId: string): string {
  const match = spaceId.trim().match(/^(\d{4})\s+(\d{4})\s+(\d{4})\s+(\d{4})/)
  if (!match) return spaceId.replace(/\s+/g, '-')
  return `${match[1]}-${match[2]}-${match[3]}-${match[4]}`
}

export function spaceIdToDefaultSlug(spaceId: string): string {
  return `${spaceIdToDbName(spaceId)}-${DEFAULT_SPACE_SLUG_SUFFIX}`
}

export function isDefaultSpaceSlug(value: string): boolean {
  return new RegExp(`^\\d{4}-\\d{4}-\\d{4}-\\d{4}-(${DEFAULT_SPACE_SLUG_SUFFIX}|${LEGACY_SPACE_SLUG_SUFFIX})$`).test(value.trim())
}

export async function resolveAnonymousLoginGateBeforeAccountEntry(input: {
  acceptedSessionOwnerId: string | null
  shouldFlushAnonymousEditor: boolean
  flushAnonymousEditor: () => Promise<void>
  listMeaningfulAnonymousRows: () => Promise<AnonymousLoginGateRow[]>
  labelRow: (row: AnonymousLoginGateRow) => string
  prompt: (labels: string[]) => Promise<AnonymousLoginGateChoice>
  importAnonymousRows: () => Promise<void>
  clearAnonymousRows: () => Promise<void>
  deleteAnonymousRows: () => Promise<void>
  resetAfterDelete: () => Promise<void>
  cancelAccountEntry: () => Promise<void>
}): Promise<boolean> {
  if (!input.acceptedSessionOwnerId) return false
  if (input.shouldFlushAnonymousEditor) {
    await input.flushAnonymousEditor()
  }

  const rows = await input.listMeaningfulAnonymousRows()
  if (!rows.length) {
    await input.clearAnonymousRows()
    return true
  }

  const choice = await input.prompt(rows.map(input.labelRow))
  if (choice === 'cancel') {
    await input.cancelAccountEntry()
    return false
  }
  if (choice === 'import') {
    await input.importAnonymousRows()
    await input.clearAnonymousRows()
    return true
  }

  await input.deleteAnonymousRows()
  await input.resetAfterDelete()
  return true
}

export async function resolveDeactivatedLoginLocalMemoryGate(input: {
  acceptedSessionOwnerId: string | null
  shouldFlushAnonymousEditor: boolean
  flushAnonymousEditor: () => Promise<void>
  listMeaningfulAnonymousRows: () => Promise<AnonymousLoginGateRow[]>
  labelRow: (row: AnonymousLoginGateRow) => string
  prompt: (labels: string[]) => Promise<DeactivatedLoginLocalMemoryChoice>
  exportAnonymousRows: () => Promise<boolean>
  clearAnonymousRows: () => Promise<void>
  deleteAnonymousRows: () => Promise<void>
  resetAfterDelete: () => Promise<void>
  cancelAccountEntry: () => Promise<void>
}): Promise<boolean> {
  if (!input.acceptedSessionOwnerId) return false
  if (input.shouldFlushAnonymousEditor) {
    await input.flushAnonymousEditor()
  }

  const rows = await input.listMeaningfulAnonymousRows()
  if (!rows.length) {
    await input.clearAnonymousRows()
    return true
  }

  const choice = await input.prompt(rows.map(input.labelRow))
  if (choice === 'cancel') {
    await input.cancelAccountEntry()
    return false
  }
  if (choice === 'export-and-continue') {
    return input.exportAnonymousRows()
  }

  await input.deleteAnonymousRows()
  await input.resetAfterDelete()
  return true
}
